import { Stack, Group, Text, Title, Button, ThemeIcon } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconCalendarCog } from '@tabler/icons-react';
import { MealPlanPreferences, PlannedPenciledMeal } from '../../../types/mealPlan';
import { SpecialOccasionsStep } from './SpecialOccasionsStep';
import { SpecialConsiderationsStep } from './SpecialConsiderationsStep';

export type ExtrasValue = {
  customRequests?: string;
  events?: MealPlanPreferences['events'];
  usePantryIngredients?: boolean;
};

type ExtrasStepProps = {
  value: ExtrasValue;
  onChange: (value: ExtrasValue) => void;
  onNext: () => void;
  onBack: () => void;
  dateRange: [string | null, string | null];
  uniqueMeals?: number;
  pantryStats: { staples: number; ingredients: number };
  // Penciled nights can't take events (generation doesn't plan them)
  penciledMeals?: PlannedPenciledMeal[];
  // Offered behind a tap on the requests section; see SpecialConsiderationsStep
  lastCustomRequest?: string | null;
};

const noop = () => {};

export const ExtrasStep = ({
  value,
  onChange,
  onNext,
  onBack,
  dateRange,
  uniqueMeals,
  pantryStats,
  penciledMeals,
  lastCustomRequest,
}: ExtrasStepProps) => {
  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconCalendarCog size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Anything else this week?
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            Special requests, guests, pantry. All optional.
          </Text>
        </Stack>
      </Stack>

      {/* Preferences & requests */}
      <SpecialConsiderationsStep
        embedded
        sections={['requests']}
        lastCustomRequest={lastCustomRequest}
        value={{
          customRequests: value.customRequests,
          usePantryIngredients: value.usePantryIngredients,
          dateRange,
        }}
        onChange={(considerations: {
          customRequests?: string;
          usePantryIngredients?: boolean;
        }) =>
          onChange({
            ...value,
            customRequests: considerations.customRequests,
            usePantryIngredients: considerations.usePantryIngredients,
          })
        }
        onNext={noop}
        onBack={noop}
        isLastStep={false}
        pantryStats={pantryStats}
        preferences={{ uniqueMeals }}
      />

      {/* Guests & occasions */}
      <SpecialOccasionsStep
        embedded
        value={{ events: value.events, dateRange }}
        onChange={(occasions: { events?: MealPlanPreferences['events'] }) =>
          onChange({ ...value, events: occasions.events })
        }
        onNext={noop}
        onBack={noop}
        isLastStep={false}
        penciledMeals={penciledMeals}
      />

      {/* Use pantry items (self-hides when the pantry is empty) */}
      <SpecialConsiderationsStep
        embedded
        sections={['pantry']}
        value={{
          customRequests: value.customRequests,
          usePantryIngredients: value.usePantryIngredients,
          dateRange,
        }}
        onChange={(considerations: {
          customRequests?: string;
          usePantryIngredients?: boolean;
        }) =>
          onChange({
            ...value,
            customRequests: considerations.customRequests,
            usePantryIngredients: considerations.usePantryIngredients,
          })
        }
        onNext={noop}
        onBack={noop}
        isLastStep={false}
        pantryStats={pantryStats}
        preferences={{ uniqueMeals }}
      />

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button
          variant="subtle"
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>

        <Button
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={onNext}
          color="orange"
        >
          Continue
        </Button>
      </Group>
    </Stack>
  );
};
